import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, Sparkles, History, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/kit";
import { PastChats } from "@/components/PastChats";
import { MicButton, VoiceToggle, ReplayButton } from "@/components/VoiceControls";
import { useRecorder, canRecord } from "@/hooks/useRecorder";
import { useMiaVoice } from "@/hooks/useMiaVoice";
import { HelpedQuoteButton } from "@/components/playbook/HelpedQuoteButton";

const newSessionId = (uid) => `chat-${uid}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const PROMPTS = ["I'm feeling anxious", "Help me sleep better", "I need motivation", "I had a rough day"];
const ANALYSES = [["patterns", "🔍 Patterns"], ["forecast", "🔮 Forecast"], ["suggestions", "💡 Suggestions"]];

const CrisisBanner = ({ onClose }) => (
  <div className="mx-4 mt-4 p-4 rounded-2xl border border-mm-error text-sm mm-fade-up" style={{ background: "color-mix(in srgb, var(--mm-error) 10%, var(--mm-surface))" }} role="alert" data-testid="ai-crisis-banner">
    <div className="font-semibold text-mm-error">You don't have to go through this alone</div>
    <p className="mt-1">Call or text <a href="tel:988" className="font-bold underline" data-testid="ai-crisis-988">988</a> (Suicide &amp; Crisis Lifeline) or text <a href="sms:741741?&body=HOME" className="font-bold underline" data-testid="ai-crisis-741741">HOME to 741741</a> — free, 24/7. In immediate danger, call 911.</p>
    <div className="flex gap-3 mt-2 text-xs font-semibold">
      <Link to="/safety" className="underline" data-testid="ai-crisis-safety-plan">Open my safety plan</Link>
      <button onClick={onClose} className="text-mm-muted" data-testid="ai-crisis-dismiss">Dismiss</button>
    </div>
  </div>
);

const Bubble = ({ role, content }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"} mm-fade-up`}>
    <div className={`max-w-[85%] px-4 py-3 rounded-2xl whitespace-pre-wrap text-sm leading-relaxed ${role === "user" ? "mm-gradient rounded-br-md" : "mm-soft rounded-bl-md"}`}>{content}</div>
  </div>
);

export default function AIChat() {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(() => newSessionId(user.id));
  const [msgs, setMsgs] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [crisis, setCrisis] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [aBusy, setABusy] = useState("");
  const end = useRef(null);
  const voice = useMiaVoice();
  const [transcribing, setTranscribing] = useState(false);

  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (m, spoken = false) => {
    const message = (m ?? text).trim();
    if (!message || busy) return;
    setText("");
    setMsgs((x) => [...x, { role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message, voice: spoken });
      const key = `v-${Date.now()}`;
      setMsgs((x) => [...x, { role: "assistant", content: data.response, vkey: key }]);
      if (data.crisis) setCrisis(true);
      if (spoken && voice.enabled) voice.play(key, data.response);
    } catch (e) {
      toast.error(errMsg(e, "Mia couldn't respond right now"));
    } finally {
      setBusy(false);
    }
  };

  const recorder = useRecorder(async (blob) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("audio", blob, `voice.${blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm"}`);
      const { data } = await api.post("/ai/transcribe", form);
      await send(data.text, true);
    } catch (e) { toast.error(errMsg(e, "Couldn't hear that. Please try again or type instead.")); }
    finally { setTranscribing(false); }
  });

  const startMic = async () => {
    voice.stop();
    if (!canRecord()) return toast.error("Voice isn't supported in this browser. You can still type to Mia.");
    try { await recorder.start(); }
    catch { toast.error("Microphone access was blocked. Allow the mic in your browser settings, or type to Mia instead."); }
  };

  const newChat = () => { voice.stop(); setSessionId(newSessionId(user.id)); setMsgs([]); setCrisis(false); setText(""); };

  const openChat = async (id) => {
    setShowPast(false);
    if (id === sessionId) return;
    setLoading(true);
    try { const { data } = await api.get(`/ai/chat-history/${id}`); setSessionId(id); setMsgs(data); setCrisis(false); }
    catch (e) { toast.error(errMsg(e, "Couldn't open that chat")); }
    finally { setLoading(false); }
  };

  const analyze = async (type) => {
    setABusy(type);
    try { const { data } = await api.post("/ai/analyze", { analysis_type: type }); setAnalysis(data); }
    catch (e) { toast.error(errMsg(e, "Analysis failed")); }
    finally { setABusy(""); }
  };

  return (
    <div data-testid="ai-page">
      <PageHeader title="Chat with Mia" subtitle="Your compassionate AI companion. Not a substitute for professional care."
        right={
          <div className="flex gap-2">
            <VoiceToggle enabled={voice.enabled} onToggle={voice.toggle} />
            <button className="mm-btn-ghost !px-3 text-sm" onClick={() => setShowPast(true)} data-testid="ai-past-chats-button"><History size={16} /><span className="hidden sm:inline whitespace-nowrap">Past chats</span></button>
            {msgs.length > 0 && <button className="mm-btn-ghost !px-3 text-sm" onClick={newChat} disabled={busy} data-testid="ai-new-chat-button"><Plus size={16} /><span className="hidden sm:inline whitespace-nowrap">New chat</span></button>}
          </div>
        } />
      <PastChats open={showPast} onOpenChange={setShowPast} activeId={sessionId} onPick={openChat} onDeleted={(id) => id === sessionId && newChat()} />
      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        <div className="mm-card flex flex-col h-[62vh]">
          <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="ai-messages">
            {loading && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-mm-primary" /></div>}
            {!loading && msgs.length === 0 && (
              <div className="text-center py-10">
                <div className="text-5xl">💜</div>
                <p className="font-semibold mt-3">Hi, I'm Mia. What's on your mind?</p>
                <div className="flex flex-wrap justify-center gap-2 mt-5">
                  {PROMPTS.map((p, i) => <button key={p} className="mm-chip" onClick={() => send(p)} data-testid={`ai-quick-prompt-${i}`}>{p}</button>)}
                </div>
              </div>
            )}
            {crisis && <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-2 bg-[var(--mm-surface)]"><CrisisBanner onClose={() => setCrisis(false)} /></div>}
            {msgs.map((m, i) => {
              const k = m.vkey || m.id || `m-${i}`;
              return (
                <div key={k}>
                  <Bubble {...m} />
                  {m.role !== "user" && m.content && i > 0 && <HelpedQuoteButton text={m.content} source="mia" testId={`ai-helped-${i}`} className="ml-2 mt-1" />}
                  {m.role !== "user" && voice.enabled && (
                    <ReplayButton id={i} playing={voice.playing === k} loading={voice.loadingKey === k} onPlay={() => voice.play(k, m.content)} onStop={voice.stop} />
                  )}
                </div>
              );
            })}
            {busy && <div className="mm-soft inline-flex px-4 py-3 rounded-2xl" data-testid="ai-typing"><Loader2 size={16} className="animate-spin text-mm-primary" /></div>}
            <div ref={end} />
          </div>
          {recorder.state === "recording" && <div className="px-4 pt-2 text-xs text-mm-error font-medium" role="status" data-testid="ai-mic-listening">Listening… tap the red button to send (stops on its own after 60 seconds)</div>}
          {transcribing && <div className="px-4 pt-2 text-xs text-mm-muted" role="status">Turning your words into text…</div>}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t flex gap-2">
            <MicButton state={transcribing ? "transcribing" : recorder.state} seconds={recorder.seconds} max={recorder.max} onStart={startMic} onStop={recorder.stop} disabled={busy} />
            <input className="mm-input" placeholder="Type or tap the mic…" value={text} onChange={(e) => setText(e.target.value)} disabled={recorder.state === "recording"} data-testid="ai-chat-input" />
            <button className="mm-btn !px-4" disabled={busy || !text.trim()} data-testid="ai-send-button"><Send size={18} /></button>
          </form>
        </div>
        <div className="mm-card p-5 h-fit">
          <h3 className="font-semibold flex items-center gap-2"><Sparkles size={18} className="text-mm-primary" /> AI Insights</h3>
          <p className="text-xs text-mm-muted mt-1">Based on your recent check-ins</p>
          <div className="flex flex-col gap-2 mt-4">
            {ANALYSES.map(([t, l]) => (
              <button key={t} className="mm-btn-ghost justify-start" onClick={() => analyze(t)} disabled={!!aBusy} data-testid={`ai-analyze-${t}`}>
                {aBusy === t ? <Loader2 size={16} className="animate-spin" /> : null}{l}
              </button>
            ))}
          </div>
          {analysis && <div className="mm-soft p-4 mt-4 text-sm whitespace-pre-wrap max-h-80 overflow-y-auto" data-testid="ai-analysis-result">{analysis.analysis}</div>}
        </div>
      </div>
    </div>
  );
}
